<template>
  <block
    component-name="block-carousel-bath-concept"
    ref="tpl_root"
    class="invisible"
    :px="false"
    body-class="overflow-hidden relative top-0 left-0 w-full h-full flex px-edge py-3xl"
    :inert="animationActive || undefined"
  >
    <template v-slot:default="blockData">
      <div class="flex gap-3xl w-full pt-20">
        <client-only>
          <div
            class="absolute inset-0 w-full h-full grid grid-cols-1 grid-rows-1"
            tab-index="-1"
            aria-hidden="true"
          >
            <div
              ref="tpl_bgBack"
              class="row-start-1 row-end-2 col-start-1 col-end-2 w-full h-full opacity-0 bg-transparent [background:radial-gradient(rgba(0,0,0,0),rgba(0,0,0,0.42))]"
            ></div>

            <div
              ref="tpl_bgFront"
              class="row-start-1 row-end-2 col-start-1 col-end-2 w-full h-full opacity-0 bg-transparent [background:radial-gradient(rgba(0,0,0,0),rgba(0,0,0,0.42))]"
            ></div>
          </div>
        </client-only>

        <!-- HEADER -->
        <header
          ref="tpl_header"
          class="grow relative top-0 left-0 flex flex-col justify-between gap-md min-w-min w-full max-w-[450px]"
        >
          <btn-pill
            ref="tpl_backBtn"
            :title="$t.labels.view_all_projects"
            icon="thumbs-grid"
            :action="{ to: '/bath-concept' }"
          />

          <div class="flex flex-col gap-lg w-full max-w-full">
            <client-only>
              <div class="flex flex-col gap-sm w-full max-w-full">
                <div
                  ref="tpl_projectName"
                  class="grid grid-cols-1 grid-rows-1 w-full overflow-hidden"
                >
                  <h1
                    class="row-start-1 row-end-2 col-start-1 col-end-2 flex items-end text-heading-4 max-w-full"
                  ></h1>
                  <div
                    inert
                    class="row-start-1 row-end-2 col-start-1 col-end-2 flex items-end text-heading-4 max-w-full"
                  ></div>
                </div>
              </div>
            </client-only>

            <div class="contents">
              <btn-pill
                :filled="true"
                :title="$t.labels.info_and_products"
                @click="showDetails = true"
              />
              <modal-bath-concept-details
                :is-open="showDetails"
                :slug="currentSlug"
                @close="showDetails = false"
              />
            </div>
          </div>
        </header>
        <!-- end HEADER -->

        <!-- CONTENT -->
        <div
          class="isolate grow relative top-0 left-0 min-w-[45%] w-full h-full"
          :class="{
            'z-2': itemsAnimationDirection === 'next',
            'z-1': itemsAnimationDirection === 'prev',
          }"
        >
          <!-- SIZERS -->
          <div
            inert
            class="absolute inset-0 z-20 w-full h-full flex justify-center items-center invisible"
          >
            <span
              ref="tpl_leftDeckSqImgSizer"
              class="block aspect-1/1 squared border-4 border-orange-500"
              :class="{
                'w-auto h-full max-h-full': leftDeckW >= leftDeckH,
                'w-full max-w-full h-auto': leftDeckW < leftDeckH,
              }"
            ></span>
          </div>

          <div
            inert
            class="absolute inset-0 z-30 w-full h-full flex justify-center items-center invisible"
          >
            <span
              ref="tpl_leftDeckRectImgSizer"
              class="block aspect-4/5 rectangular border-2 border-blue-500"
              :class="{
                'w-full max-w-full h-auto': leftDeckW * 1.2 < leftDeckH,
                'w-auto h-full max-h-full': leftDeckW * 1.2 >= leftDeckH,
              }"
            ></span>
          </div>
          <!-- end SIZERS -->

          <div
            ref="tpl_leftDeckBackWrapper"
            inert
            class="absolute inset-0 z-3 w-full h-full flex justify-center items-center"
            :class="{
              '[&>img.squared]:w-auto [&>img.squared]:max-w-none [&>img.squared]:h-full [&>img.squared]:max-h-full':
                leftDeckW >= leftDeckH,
              '[&>img.squared]:w-full [&>img.squared]:max-w-full [&>img.squared]:h-auto [&>img.squared]:max-h-none':
                leftDeckW < leftDeckH,
              '[&>img.rectangular]:w-full [&>img.rectangular]:max-w-full [&>img.rectangular]:h-auto [&>img.rectangular]:max-h-none':
                leftDeckW * 1.2 < leftDeckH,
              '[&>img.rectangular]:w-auto [&>img.rectangular]:max-w-none [&>img.rectangular]:h-full [&>img.rectangular]:max-h-full':
                leftDeckW * 1.2 >= leftDeckH,
            }"
          ></div>

          <div
            ref="tpl_leftDeckFrontWrapper"
            class="absolute inset-0 z-4 w-full h-full flex justify-center items-center pointer-events-none"
            :class="{
              '[&>img]:pointer-events-auto [&>img]:cursor-zoom-in':
                !animationActive,
              '[&>img.squared]:w-auto [&>img.squared]:max-w-none [&>img.squared]:h-full [&>img.squared]:max-h-full':
                leftDeckW >= leftDeckH,
              '[&>img.squared]:w-full [&>img.squared]:max-w-full [&>img.squared]:h-auto [&>img.squared]:max-h-none':
                leftDeckW < leftDeckH,
              '[&>img.rectangular]:w-full [&>img.rectangular]:max-w-full [&>img.rectangular]:h-auto [&>img.rectangular]:max-h-none':
                leftDeckW * 1.2 < leftDeckH,
              '[&>img.rectangular]:w-auto [&>img.rectangular]:max-w-none [&>img.rectangular]:h-full [&>img.rectangular]:max-h-full':
                leftDeckW * 1.2 >= leftDeckH,
            }"
            @click="zoomOutItem"
          ></div>

          <modal-bath-concept-zoom
            ref="tpl_modalZoom"
            :is-open="showZoom"
            :items="allImagesArray[projectIndex.current]"
            :item-index="itemIndex.current"
            :fit-element="
              itemIndex.current === 0 || itemIndex.current === 4
                ? tpl_leftDeckSqImgSizer
                : tpl_leftDeckRectImgSizer
            "
            @close="showZoom = false"
            @update="swapItems"
          />

          <!-- TRANSITION FIX WRAPPER -->
          <div ref="tpl_transitionFixWrapper" inert></div>
          <!-- end TRANSITION FIX WRAPPER -->
        </div>
        <!-- end CONTENT -->

        <!-- FOOTER -->
        <footer
          ref="tpl_footer"
          class="isolate grow relative top-0 left-0 flex flex-col justify-between items-end gap-lg min-w-min w-full max-w-[450px]"
          :class="{
            'z-1': itemsAnimationDirection === 'next',
            'z-2': itemsAnimationDirection === 'prev',
          }"
        >
          <div
            class="max-w-min transition-opacity ease-in-out"
            :class="{
              'duration-500': releatedBySlug?.length,
              'duration-400 opacity-0': !releatedBySlug?.length,
            }"
            :inert="!releatedBySlug?.length ? true : undefined"
          >
            <btn-pill
              ref="tpl_relatedBtn"
              :title="$t.labels.related_projects"
              class="self-end"
              :disabled="!releatedBySlug?.length"
              @click="showRelated = true"
            />
            <modal-bath-concept-related
              :is-open="showRelated"
              :items="releatedBySlug"
              @close="showRelated = false"
            />
          </div>

          <div
            class="flex flex-col justify-center gap-lg w-full sizer-max-w grow"
          >
            <div class="relative top-0 left-0 aspect-4/5 w-full">
              <!-- SIZER -->
              <div
                inert
                class="absolute inset-0 z-20 w-full h-full flex justify-center items-end invisible"
              >
                <span
                  ref="tpl_rightDeckSqImgSizer"
                  class="block aspect-1/1 squared border-4 border-orange-500 w-full max-w-full h-auto"
                ></span>
              </div>
              <!-- end SIZER -->

              <div
                ref="tpl_rightDeckBackWrapper"
                inert
                class="absolute z-1 inset-0 h-full w-full flex justify-end items-end [&>img]:w-full [&>img]:max-w-full [&>img]:h-auto"
              ></div>

              <div
                ref="tpl_rightDeckFrontWrapper"
                class="absolute z-2 inset-0 h-full w-full flex justify-end items-end [&>img]:w-full [&>img]:max-w-full [&>img]:h-auto"
              ></div>
            </div>

            <div class="flex justify-start gap-lg w-full">
              <btn-arrow
                :disabled="animationActive"
                direction="prev"
                @click="prevItem()"
              />
              <btn-arrow
                :disabled="animationActive"
                direction="next"
                @click="nextItem()"
              />
            </div>
          </div>
          <txt-scroll-cta-bath-concept />
        </footer>
        <!-- end FOOTER -->
      </div>
    </template>
  </block>
</template>

<script setup>
import { useRootStore } from "@/stores/root";

const props = defineProps({
  fitElement: {
    type: Object,
    validator: function (value) {
      // Expected to be an HTML element
      return value instanceof HTMLElement;
    },
    required: false,
  },
  interactive: {
    type: Boolean,
    default: true,
  },
});
const colorMode = useColorMode();
const nuxtApp = useNuxtApp();
const rootStore = useRootStore();
const route = useRoute();
const { setLuminanceBathConceptProject } = useColors();
const { fromCloudinary } = useCdn();
const { sendMessage } = useWebWorker("/workers/image-loader.js");

const projects = computed(() => rootStore.projectBathroom.value.projects);

const tpl_root = templateRef("tpl_root");
const tpl_modalZoom = templateRef("tpl_modalZoom");
const tpl_bgFront = templateRef("tpl_bgFront");
const tpl_bgBack = templateRef("tpl_bgBack");
const tpl_header = templateRef("tpl_header");
const tpl_footer = templateRef("tpl_footer");
const tpl_leftDeckFrontWrapper = templateRef("tpl_leftDeckFrontWrapper");
const tpl_leftDeckBackWrapper = templateRef("tpl_leftDeckBackWrapper");
const tpl_rightDeckFrontWrapper = templateRef("tpl_rightDeckFrontWrapper");
const tpl_rightDeckBackWrapper = templateRef("tpl_rightDeckBackWrapper");
const tpl_leftDeckSqImgSizer = templateRef("tpl_leftDeckSqImgSizer");
const tpl_leftDeckRectImgSizer = templateRef("tpl_leftDeckRectImgSizer");
const tpl_rightDeckSqImgSizer = templateRef("tpl_rightDeckSqImgSizer");
const tpl_transitionFixWrapper = templateRef("tpl_transitionFixWrapper");
const tpl_backBtn = templateRef("tpl_backBtn");
const tpl_relatedBtn = templateRef("tpl_relatedBtn");
const tpl_projectName = templateRef("tpl_projectName");

const showZoom = ref(false);
const showDetails = ref(false);
const showRelated = ref(false);
const itemsAnimationDirection = ref("next");
const animationActive = ref(false);
const slugOverride = ref(props.fitElement?.dataset.slug);

const { width: leftDeckW, height: leftDeckH } = useElementSize(
  tpl_leftDeckFrontWrapper
);

const { width: rightDeckW, height: rightDeckH } = useElementSize(
  tpl_rightDeckFrontWrapper
);

const currentSlug = computed(() => {
  return slugOverride.value ?? route.params.id;
});

/* DEFINE CURRENT PROJECT INDEX */
const projectIndex = ref({
  current: projects.value.findIndex(
    (project) => project.slug === currentSlug.value
  ),
});

const MAX_PROJECT_INDEX = projects.value.length - 1;
const MAX_ITEM_INDEX = 4;

projectIndex.value.prev =
  projectIndex.value.current === 0
    ? MAX_PROJECT_INDEX
    : projectIndex.value.current - 1;
projectIndex.value.next =
  projectIndex.value.current === MAX_PROJECT_INDEX
    ? 0
    : projectIndex.value.current + 1;
/* end DEFINE CURRENT PROJECT INDEX */

const itemIndex = ref({
  prev:
    props.fitElement?.dataset?.type === "moodboard"
      ? MAX_ITEM_INDEX - 1
      : MAX_ITEM_INDEX,
  current: props.fitElement?.dataset?.type === "moodboard" ? MAX_ITEM_INDEX : 0,
  next: props.fitElement?.dataset?.type === "moodboard" ? 0 : 1,
});

const allImagesArray = computed(() => {
  const list = [];
  for (let i = 0, len = projects.value.length; i < len; i++) {
    list.push([
      projects.value[i].cover.asset,
      ...projects.value[i].items,
      projects.value[i].moodboard.asset,
    ]);
  }
  return list;
});

const bgColor = computed(() => {
  return setLuminanceBathConceptProject(
    projects.value[projectIndex.value.current].megacolor
  );
});

const releatedBySlug = computed(() => {
  return projects.value.filter((project) =>
    projects.value[projectIndex.value.current].related_by_style.includes(
      project.slug
    )
  );
});

let projectsAnimationDirection, changeProjectObserver, tl;

/* DOM UTILS */
function cloneFromImgsPool(projectIndex, itemIndex) {
  const item = allImagesArray.value[projectIndex][itemIndex];
  const img = document.createElement("img");
  img.alt = item.alt;
  img.src = fromCloudinary(item.src, { width: 1000 });
  img.width = item.width;
  img.height = item.height;
  img.loading = "eager";
  if (item.aspectRatio === "1 / 1") {
    img.className = "block aspect-1/1 squared";
  } else if (item.aspectRatio === "4 / 5") {
    img.className = "block aspect-4/5 rectangular";
  }
  return img;
}

function nodeMover(el, parent) {
  while (parent.firstChild) {
    parent.removeChild(parent.firstChild);
  }

  if (el) parent.appendChild(el);
}
/* end DOM UTILS */

/* HANDLE ANIMATIONS */
function handleGSAPtimeline(
  onStart,
  animationsArray,
  onComplete,
  instant = false,
  interactivityResumeDelay = 0.75
) {
  if (tl) {
    tl.kill();
    tl = null;
  }

  if (animationsArray && animationsArray.length) {
    tl = gsap.timeline({
      onStart: () => {
        animationActive.value = true;
        if (onStart) onStart();
      },
      onComplete: () => {
        if (onComplete) onComplete();

        gsap.delayedCall(interactivityResumeDelay, () => {
          animationActive.value = false;
        });
      },
    });

    for (let i = 0, len = animationsArray.length; i < len; i++) {
      tl.add(
        animationsArray[i].child,
        animationsArray[i].position !== undefined
          ? animationsArray[i].position
          : ">"
      );
    }

    if (instant) {
      tl.totalProgress(1);
    }
  }
}
/* end HANDLE ANIMATIONS */

/* PROJECTS SWAP */
function swapProject() {
  // stop interactivity
  animationActive.value = true;
  const pastBgColor = gsap.getProperty(tpl_bgFront.value, "backgroundColor");

  nodeMover(
    cloneFromImgsPool(projectIndex.value.current, itemIndex.value.current),
    tpl_leftDeckBackWrapper.value
  );

  nodeMover(
    cloneFromImgsPool(projectIndex.value.current, itemIndex.value.next),
    tpl_rightDeckBackWrapper.value
  );

  let direction = projectsAnimationDirection === "prev" ? "prev" : "next";
  projectsAnimationDirection = null;

  projectIndex.value.current = projects.value.findIndex(
    (project) => project.slug === currentSlug.value
  );

  projectIndex.value.next =
    projectIndex.value.current === MAX_PROJECT_INDEX
      ? 0
      : projectIndex.value.current + 1;

  projectIndex.value.prev =
    projectIndex.value.current === 0
      ? MAX_PROJECT_INDEX
      : projectIndex.value.current - 1;

  nodeMover(
    cloneFromImgsPool(projectIndex.value.current, itemIndex.value.current),
    tpl_leftDeckFrontWrapper.value
  );

  nodeMover(
    cloneFromImgsPool(projectIndex.value.current, itemIndex.value.next),
    tpl_rightDeckFrontWrapper.value
  );

  nextTick(() => {
    handleGSAPtimeline(
      () => clearFixCanvas(),
      [
        {
          child: gsap.set(tpl_bgBack.value, {
            opacity: 1,
            backgroundColor: pastBgColor,
          }),
          position: 0,
        },

        {
          child: gsap.fromTo(
            tpl_projectName.value.lastChild,
            {
              text: tpl_projectName.value.firstChild.innerText,
              scale: 1,
              opacity: 1,
              transformOrigin: "center",
            },
            { scale: 0.78, opacity: 0, duration: 0.6, ease: "power2.inOut" }
          ),
          position: 0,
        },

        {
          child: gsap.fromTo(
            tpl_projectName.value.firstChild,
            {
              text: projects.value[projectIndex.value.current].name,
              yPercent: direction === "next" ? 120 : -120,
            },
            { yPercent: 0, duration: 1.1, ease: "power3.inOut" }
          ),
          position: 0,
        },

        {
          child: gsap.fromTo(
            [tpl_leftDeckFrontWrapper.value, tpl_rightDeckFrontWrapper.value],
            {
              clipPath:
                direction === "next"
                  ? "inset(0% 0% 100% 0%)"
                  : "inset(100% 0% 0% 0%)",
            },
            {
              clipPath: "inset(0% 0% 0% 0%)",
              duration: 0.615,
              ease: "power1.inOut",
            }
          ),
          position: 0,
        },

        {
          child: gsap.fromTo(
            tpl_bgFront.value,
            {
              opacity: 1,
              backgroundColor: bgColor.value,
              clipPath:
                direction === "next"
                  ? "inset(0% 0% 100% 0%)"
                  : "inset(100% 0% 0% 0%)",
            },
            {
              clipPath: "inset(0% 0% 0% 0%)",
              duration: 0.8,
              ease: "power2.inOut",
            }
          ),
          position: "<0.2",
        },

        {
          child: gsap.fromTo(
            tpl_bgFront.value,
            {
              webkitFilter:
                colorMode.value == "light"
                  ? "brightness(0.92)"
                  : "brightness(1.3)",
              filter:
                colorMode.value == "light"
                  ? "brightness(0.92)"
                  : "brightness(1.3)",
            },
            {
              webkitFilter: "brightness(1)",
              filter: "brightness(1)",
              duration: 0.8,
              ease: "sine.inOut",
            }
          ),
          position: ">-0.12",
        },
      ],
      () => {
        gsap.set(tpl_projectName.value.lastChild, {
          text: "",
          scale: 1,
          opacity: 1,
          transformOrigin: "center",
        });

        gsap.set(
          [tpl_leftDeckFrontWrapper.value, tpl_rightDeckFrontWrapper.value],
          {
            clearProps: "all",
          }
        );

        gsap.set(tpl_bgFront.value, {
          clearProps: "clip-path",
        });

        gsap.set(tpl_bgBack.value, { backgroundColor: "transparent" });

        // empty back
        nodeMover(null, tpl_leftDeckBackWrapper.value);
        nodeMover(null, tpl_rightDeckBackWrapper.value);
      }
    );
  });
}
/* end PROJECTS SWAP */

/* ITEMS SWAP */
function swapItems(
  index = 0,
  animation = false,
  direction = false,
  allowSameIndex = false
) {
  if (itemIndex.value.current === index && !allowSameIndex) {
    return;
  }

  const pastItemIndex = JSON.parse(JSON.stringify(itemIndex.value));

  // update itemIndex
  itemIndex.value.current = index;
  itemIndex.value.next = index === MAX_ITEM_INDEX ? 0 : index + 1;
  itemIndex.value.prev = index === 0 ? MAX_ITEM_INDEX : index - 1;

  if (tpl_leftDeckFrontWrapper.value?.firstChild) {
    appendTransitionFixCanvas(tpl_leftDeckFrontWrapper.value.firstChild, 1);
    appendTransitionFixCanvas(tpl_rightDeckFrontWrapper.value.firstChild, 1);
  }

  /* LAYOUT */
  nodeMover(
    cloneFromImgsPool(projectIndex.value.current, itemIndex.value.current),
    tpl_leftDeckFrontWrapper.value
  );

  nodeMover(
    cloneFromImgsPool(projectIndex.value.current, itemIndex.value.next),
    tpl_rightDeckFrontWrapper.value
  );
  /* end LAYOUT */
  itemsAnimationDirection.value = direction ? direction : "next";

  if (animation) {
    if (itemsAnimationDirection.value === "next") {
      // NEXT ITEM ANIMATION
      nodeMover(
        cloneFromImgsPool(projectIndex.value.current, pastItemIndex.current),
        tpl_leftDeckBackWrapper.value
      );
      nodeMover(null, tpl_rightDeckBackWrapper.value);

      nextTick(() => {
        handleGSAPtimeline(
          () => clearFixCanvas(),
          [
            {
              child: Flip.fit(
                tpl_leftDeckFrontWrapper.value.firstChild,
                itemIndex.value.current === 0 || itemIndex.value.current === 4
                  ? tpl_rightDeckSqImgSizer.value
                  : tpl_rightDeckFrontWrapper.value,
                { scale: true }
              ),
              position: 0,
            },

            {
              child: Flip.fit(
                tpl_leftDeckFrontWrapper.value.firstChild,
                itemIndex.value.current === 0 || itemIndex.value.current === 4
                  ? tpl_leftDeckSqImgSizer.value
                  : tpl_leftDeckRectImgSizer.value,
                {
                  duration: 1,
                  scale: true,
                  absolute: true,
                  ease: "power3.inOut",
                }
              ),
              position: 0,
            },

            {
              child: gsap.fromTo(
                tpl_leftDeckBackWrapper.value.firstChild,
                { scale: 1, autoAlpha: 1, rotation: 0 },
                {
                  duration: 1,
                  scale: 0.55,
                  rotation: -8,
                  autoAlpha: 0,
                  ease: "power3.inOut",
                }
              ),
              position: 0,
            },

            {
              child: gsap.fromTo(
                tpl_rightDeckFrontWrapper.value.firstChild,
                { scale: 0.55, autoAlpha: 0, rotation: 8 },
                {
                  duration: 1,
                  scale: 1,
                  autoAlpha: 1,
                  rotation: 0,
                  ease: "power3.inOut",
                }
              ),
              position: 0,
            },
          ],
          () => {
            gsap.set(
              [
                tpl_leftDeckFrontWrapper.value.firstChild,
                tpl_rightDeckFrontWrapper.value.firstChild,
              ],
              {
                clearProps: "all",
              }
            );

            gsap.set(tpl_bgFront.value, {
              clearProps: "clip-path",
            });

            nodeMover(null, tpl_leftDeckBackWrapper.value);
          },
          false,
          0.2
        );
      });
    } else {
      // PREV ITEM ANIMATION
      nodeMover(null, tpl_leftDeckBackWrapper.value);

      nodeMover(
        cloneFromImgsPool(projectIndex.value.current, pastItemIndex.next),
        tpl_rightDeckBackWrapper.value
      );

      nextTick(() => {
        handleGSAPtimeline(
          () => clearFixCanvas(),
          [
            {
              child: Flip.fit(
                tpl_rightDeckFrontWrapper.value.firstChild,
                itemIndex.value.next === 0 || itemIndex.value.next === 4
                  ? tpl_leftDeckSqImgSizer.value
                  : tpl_leftDeckRectImgSizer.value,
                { scale: true }
              ),
              position: 0,
            },

            {
              child: Flip.fit(
                tpl_rightDeckFrontWrapper.value.firstChild,
                itemIndex.value.next === 0 || itemIndex.value.next === 4
                  ? tpl_rightDeckSqImgSizer.value
                  : tpl_rightDeckFrontWrapper.value,
                {
                  duration: 1,
                  absolute: true,
                  scale: true,
                  ease: "power3.inOut",
                }
              ),
              position: 0,
            },

            {
              child: gsap.to(tpl_rightDeckBackWrapper.value.firstChild, {
                duration: 1.2,
                scale: 0.55,
                autoAlpha: 0,
                rotation: 8,
                ease: "power3.inOut",
              }),
              position: 0,
            },

            {
              child: gsap.fromTo(
                tpl_leftDeckFrontWrapper.value.firstChild,
                { scale: 0.55, autoAlpha: 0, rotation: -8 },
                {
                  duration: 1.2,
                  scale: 1,
                  autoAlpha: 1,
                  rotation: 0,
                  ease: "power3.inOut",
                }
              ),
              position: 0,
            },
          ],
          () => {
            gsap.set(
              [
                tpl_leftDeckFrontWrapper.value.firstChild,
                tpl_rightDeckFrontWrapper.value.firstChild,
              ],
              {
                clearProps: "all",
              }
            );

            nodeMover(null, tpl_rightDeckBackWrapper.value);
          },
          false,
          0.2
        );
      });
    }
  } else {
    // NO ANIMATION
    nodeMover(null, tpl_leftDeckBackWrapper.value);
    nodeMover(null, tpl_rightDeckBackWrapper.value);
    clearFixCanvas();
  }

  /* end LAYOUT */
}
/* end ITEMS SWAP */

function clearFixCanvas() {
  requestAnimationFrame(() => {
    while (tpl_transitionFixWrapper.value.firstChild) {
      tpl_transitionFixWrapper.value.removeChild(
        tpl_transitionFixWrapper.value.firstChild
      );
    }
  });
}

/* NEXT ITEM */
function nextItem() {
  swapItems(
    itemIndex.value.current < MAX_ITEM_INDEX ? itemIndex.value.current + 1 : 0,
    true,
    "next"
  );
}
/* end NEXT ITEM */

/* PREV ITEM */
function prevItem() {
  swapItems(
    itemIndex.value.current > 0 ? itemIndex.value.current - 1 : MAX_ITEM_INDEX,
    true,
    "prev"
  );
}
/* end PREV ITEM */

/* ZOOM ITEM */
function zoomOutItem() {
  showZoom.value = true;

  nextTick(() => {
    tpl_modalZoom.value.zoom(true);
  });
}
/* end ZOOM ITEM */

/* ENTER COREOGRAPHY */
function doEnterCoreography() {
  let enterCoreoTL = gsap.timeline({
    onStart: () => {
      animationActive.value = true;
      tpl_root.value.$el.classList.remove("invisible");
      nextTick(() => {
        // mette invisibile la card webgl
        window.dispatchEvent(new CustomEvent("coreography-start"));
      });
    },
    onComplete: () => {
      gsap.set(
        [
          tpl_leftDeckFrontWrapper.value.firstChild,
          tpl_rightDeckFrontWrapper.value.firstChild,
        ],
        {
          clearProps: "all",
        }
      );
      animationActive.value = false;
      // resetta la card webgl
      window.dispatchEvent(new CustomEvent("coreography-complete"));
      slugOverride.value = null;
    },
  });

  enterCoreoTL.add(
    Flip.fit(tpl_leftDeckFrontWrapper.value.firstChild, props.fitElement, {
      scale: true,
    })
  );

  enterCoreoTL.add(
    Flip.fit(
      tpl_leftDeckFrontWrapper.value.firstChild,
      tpl_leftDeckSqImgSizer.value,
      {
        duration: 0.35,
        scale: true,
        absolute: true,
        ease: "power1.out",
      },
      0
    )
  );

  enterCoreoTL.fromTo(
    tpl_bgFront.value,
    {
      opacity: 1,
      clipPath: "inset(100% 0% 0% 0%)",
    },
    {
      clipPath: "inset(0% 0% 0% 0%)",
      duration: 0.7,
      ease: "power2.inOut",
    },
    "<0.05"
  );

  enterCoreoTL.fromTo(
    tpl_header.value,
    { xPercent: -120 },
    { duration: 1, xPercent: 0, ease: "expo.inOut" },
    "<0"
  );

  enterCoreoTL.fromTo(
    tpl_footer.value,
    { xPercent: 120 },
    { duration: 1, xPercent: 0, ease: "expo.inOut" },
    "<"
  );

  enterCoreoTL.fromTo(
    tpl_backBtn.value.$el,
    { x: -300 },
    { duration: 1.4, x: 0, ease: "expo.inOut" },
    "<"
  );

  if (projects.value[projectIndex.value.current].related_by_style?.length) {
    enterCoreoTL.fromTo(
      tpl_relatedBtn.value.$el,
      { x: 300 },
      { duration: 1.4, x: 0, ease: "expo.inOut" },
      "<"
    );
  }
}
/* end ENTER COREOGRAPHY */

function appendTransitionFixCanvas(element, opacity = 0) {
  const box = element.getBoundingClientRect();

  const canvas = document.createElement("canvas");
  canvas.width = box.width;
  canvas.height = box.height;
  canvas.style.width = box.width + "px";
  canvas.style.height = box.height + "px";
  const ctx = canvas.getContext("2d");
  ctx.drawImage(element, 0, 0, box.width, box.height);
  canvas.style.position = "fixed";
  canvas.style.zIndex = 1000;
  canvas.style.top = box.top + "px";
  canvas.style.left = box.left + "px";
  canvas.style.opacity = opacity;
  canvas.classList.add("transition-fix-clone");
  tpl_transitionFixWrapper.value.appendChild(canvas);

  return canvas;
}

watch(
  () => route.params,
  (newVal, oldVal) => {
    if (slugOverride.value) {
      return;
    }

    const canvasLeft = appendTransitionFixCanvas(
      tpl_leftDeckFrontWrapper.value.firstChild
    );
    const canvasRight = appendTransitionFixCanvas(
      tpl_rightDeckFrontWrapper.value.firstChild
    );
    // gsap to opacity 1
    gsap.to([canvasLeft, canvasRight], {
      duration: 0.06,
      opacity: 1,
      onComplete: () => {
        // kill current timeline
        handleGSAPtimeline();
        const pastIndex = {
          current: projects.value.findIndex(
            (project) => project.slug === oldVal.id
          ),
        };
        pastIndex.next =
          pastIndex.current === MAX_PROJECT_INDEX ? 0 : pastIndex.current + 1;
        swapProject(pastIndex);
      },
    });
  }
);

watch(animationActive, (newVal, oldVal) => {
  if (newVal) {
    showZoom.value = false;
    showRelated.value = false;
    showDetails.value = false;
    if (changeProjectObserver) changeProjectObserver.disable();
  } else {
    if (changeProjectObserver) changeProjectObserver.enable();
  }
});

watch(showZoom, (newVal, oldVal) => {
  if (newVal && changeProjectObserver) {
    changeProjectObserver.disable();
  } else if (changeProjectObserver) {
    changeProjectObserver.enable();
  }
});

watch(showDetails, (newVal, oldVal) => {
  if (newVal && changeProjectObserver) {
    changeProjectObserver.disable();
  } else if (changeProjectObserver) {
    changeProjectObserver.enable();
  }
});

watch(showRelated, (newVal, oldVal) => {
  if (newVal && changeProjectObserver) {
    changeProjectObserver.disable();
  } else if (changeProjectObserver) {
    changeProjectObserver.enable();
  }
});

useSafeMountedEl(
  [
    tpl_root,
    tpl_modalZoom,
    tpl_projectName,
    tpl_bgFront,
    tpl_bgBack,
    tpl_leftDeckFrontWrapper,
    tpl_leftDeckBackWrapper,
    tpl_rightDeckFrontWrapper,
    tpl_rightDeckBackWrapper,
    tpl_leftDeckSqImgSizer,
    tpl_leftDeckRectImgSizer,
    tpl_rightDeckSqImgSizer,
  ],
  () => {
    /* LAYOUT */
    swapItems(itemIndex.value.current, false, false, true);

    gsap.set(tpl_projectName.value.firstChild, {
      text: projects.value[projectIndex.value.current].name,
    });

    gsap.set(tpl_bgFront.value, { backgroundColor: bgColor.value });
    /* end LAYOUT */

    /* NAVIGATE THROUGH PROJECTS  */
    changeProjectObserver = Observer.create({
      target: window, // can be any element (selector text is fine)
      type: "wheel,touch,pointer", // comma-delimited list of what to listen for
      tolerance: 10,
      //wheelSpeed: -1,
      preventDefault: true,
      onUp: () => {
        if (
          !animationActive.value &&
          !showZoom.value &&
          !showRelated.value &&
          !showDetails.value
        ) {
          animationActive.value = true;
          projectsAnimationDirection = "next";
          navigateTo(
            `/${route.params.country}/${route.params.locale}/bath-concept/${
              projects.value[projectIndex.value.next].slug
            }`
          );
        }
      },
      onDown: () => {
        if (
          !animationActive.value &&
          !showZoom.value &&
          !showRelated.value &&
          !showDetails.value
        ) {
          animationActive.value = true;
          projectsAnimationDirection = "prev";
          navigateTo(
            `/${route.params.country}/${route.params.locale}/bath-concept/${
              projects.value[projectIndex.value.prev].slug
            }`
          );
        }
      },
    });
    /* end NAVIGATE THROUGH PROJECTS  */
    if (props.fitElement && props.fitElement?.dataset.needsDesktopEnterAnim) {
      delete props.fitElement.dataset.needsDesktopEnterAnim;
      doEnterCoreography();
    } else {
      gsap.set(tpl_bgFront.value, { opacity: 1 });
      tpl_root.value.$el.classList.remove("invisible");
    }

    let lastIndex = projectIndex.value.current;
    updateImageToLoad([projectIndex.value.next, projectIndex.value.prev]);
    watch(projectIndex.value, () => {
      // ealuete the index to preload accordong scroll direction
      const indexToPreload =
        lastIndex == projectIndex.value.next
          ? projectIndex.value.prev
          : projectIndex.value.next;
      lastIndex = projectIndex.value.current;
      updateImageToLoad([indexToPreload]);
    });

    watch(
      () => props.interactive,
      (newVal, oldVal) => {
        if (newVal) {
          changeProjectObserver.enable();
        } else {
          changeProjectObserver.disable();
        }
      }
    );
  },
  true,
  1000,
  20
);

function updateImageToLoad(indexes) {
  const preloadImages = indexes.reduce((acc, index) => {
    return [
      ...acc,
      ...allImagesArray.value[index].map((el) =>
        fromCloudinary(el.src, { width: 1000 })
      ),
    ];
  }, []);

  sendMessage(preloadImages);
}

onBeforeUnmount(() => {
  if (changeProjectObserver) {
    changeProjectObserver.kill();
    changeProjectObserver = null;
  }
});
</script>

<style scoped>
.sizer-max-w {
  max-width: 200px;
}

@media (min-height: 750px) {
  .sizer-max-w {
    max-width: 240px;
  }
}

@media (min-height: 850px) {
  .sizer-max-w {
    max-width: 300px;
  }
}
</style>
